import React from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../Redux/hooks';
import { convertDateNumberToString } from '../Business/utils/DateUtils';
import { PulseLoader } from 'react-spinners';

export interface PropsData {
    date: string;
    time: string;
    supportIdx: number;
    colorClass: string;
    grp: number;
    duration: number;
}

export interface PropsHourData {
    date: string;
    hour: string;
    colorClass: string;
    letter: string;
    data: PropsData[];
}

type Props = {
    hourData: Map<string, PropsHourData>;
    durations: number[];
    setStartDateKPI: (date: string) => void;
    setEndDateKPI: (date: string) => void;
};


const TimeTablePopOver = React.forwardRef((props: any, ref) => {
    const renderTooltipContent = (data: PropsData) => {
        const durationIdx = props.durations.findIndex((duration: number) => duration === data.duration);
        const letter = String.fromCharCode(65 + durationIdx);

        return (
            <>
                <span className={`colored-circle-timetable ${data.colorClass}`}>
                    <span className='colored-circle-timetable-text'>{letter}</span>
                </span>
                <span className="colored-circle-timetable-legend-value">{data.duration}s</span><br />
                <span className={`${data.colorClass}-text tooltipTitle`}>{data.time}</span><br />
                <span>{data.grp.toFixed(2)} GRP</span>
            </>
        );
    };

    return (
        <Tooltip ref={ref} id="tooltip" {...props}>
            <div>
                {props.data.data.map((data: PropsData, idx: number) => (
                    <div key={idx}>
                        {renderTooltipContent(data)}
                    </div>
                ))}
            </div>
        </Tooltip>
    );
})

const Timetable: React.FC<Props> = ({ hourData, durations, setStartDateKPI, setEndDateKPI }) => {
    const { t } = useTranslation();
    const locale = useAppSelector(state => state.locale.locale);
    // Initialize date formatters for display
    const dayFormatter = new Intl.DateTimeFormat(locale === "fr" ? 'fr-FR' : "en-US", { weekday: 'short' });
    const dateFormatter = new Intl.DateTimeFormat(locale === "fr" ? 'fr-FR' : "en-US", { day: 'numeric' });
    const monthFormatter = new Intl.DateTimeFormat(locale === "fr" ? 'fr-FR' : "en-US", { month: 'long' });

    const [fullDays, setFullDays] = React.useState<boolean>(true);
    const [fullHours, setFullHours] = React.useState<boolean>(true);
    const [days, setDays] = React.useState<Date[]>([]);
    const [startDate, setStartDate] = React.useState<Date>(new Date());
    const [endDate, setEndDate] = React.useState<Date>(new Date());
    const [timeSlots, setTimeslots] = React.useState<string[]>([]);
    const [dateStrings, setDateStrings] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);

    React.useEffect(() => {
        setLoading(true);
        // Extract unique dates and hours from hourData
        const uniqueDates = [...new Set([...hourData.values()].map(data => data.date))];
        uniqueDates.sort();
        
        if (uniqueDates.length > 0) {
            // Convert date strings to Date objects for display formatting
            const startDateParts = uniqueDates[0].split('-').map(Number);
            const endDateParts = uniqueDates[uniqueDates.length - 1].split('-').map(Number);
            
            const minDate = new Date(startDateParts[0], startDateParts[1] - 1, startDateParts[2]);
            const maxDate = new Date(endDateParts[0], endDateParts[1] - 1, endDateParts[2]);
            
            setStartDate(minDate);
            setEndDate(new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate() + 1));
            
            setStartDateKPI(convertDateNumberToString(minDate.getFullYear(), minDate.getMonth() + 1, minDate.getDate(), locale));
            setEndDateKPI(convertDateNumberToString(maxDate.getFullYear(), maxDate.getMonth() + 1, maxDate.getDate(), locale));
            setDateStrings(uniqueDates);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hourData]);

    // Create an array of days from start to end dates
    React.useEffect(() => {
        const dayDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const tmpDays = Array.from({ length: dayDiff }, (_, i) => {
            const date = new Date(startDate);
            date.setDate(date.getDate() + i);
            return date;
        });

        if (!fullDays) {
            // Filter days that have data
            const tmpDaysFiltered = tmpDays.filter(day => {
                const formattedDate = formatDateToString(day);
                return dateStrings.includes(formattedDate);
            });
            setDays(tmpDaysFiltered);
        } else {
            setDays(tmpDays);
        }

        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endDate, startDate, dateStrings, fullDays]);

    React.useEffect(() => {
        // Get unique hours from hourData
        const hourStrings = [...hourData.values()].map(data => {
            const hour = parseInt(data.hour.split(':')[0]);
            return hour;
        });

        // Find min and max hour
        const minHour = Math.min(...hourStrings);
        const maxHour = Math.max(...hourStrings);

        // Create time slots array
        const tmpTimeSlots = Array.from(
            { length: maxHour - minHour + 1 }, 
            (_, i) => `${(i + minHour).toString().padStart(2, '0')}:00`
        );

        if (!fullHours) {
            // Only include hours that have data
            const tmpTimeSlotsFiltered = tmpTimeSlots.filter(timeSlot => {
                const hour = parseInt(timeSlot.split(':')[0]);
                return hourStrings.includes(hour);
            });
            setTimeslots(tmpTimeSlotsFiltered);
        } else {
            setTimeslots(tmpTimeSlots);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullHours, hourData])

    const formatDateToString = (date: Date): string => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const renderCellContent = (day: Date, timeSlot: string): JSX.Element => {
        const dayString = formatDateToString(day);
        const hour = parseInt(timeSlot.split(':')[0]);
        const key = `${dayString}${hour}`;

        const foundData = hourData.get(key);

        if (foundData) {
            return (
                <OverlayTrigger placement='bottom' overlay={<TimeTablePopOver durations={durations} data={foundData} />}>
                    <span className={`colored-circle-timetable ${foundData.colorClass}`}>
                        <span className='colored-circle-timetable-text'>{foundData.letter}</span>
                    </span>
                </OverlayTrigger>
            );
        }

        return <span></span>;
    }

    const renderDurationLegend = (duration: number, idx: number) => {
        const upperLetter = String.fromCharCode(65 + idx);
        return (
            <span className="durationLegend" key={idx}><span className={`colored-circle-timetable grey`}>
                <span className='colored-circle-timetable-legend'>{upperLetter}</span>
            </span>
                <span className="colored-circle-timetable-legend-value">{duration}s</span></span>
        );
    }

    return (
        <div>
            <div className="timetable-config-header">
                <Container fluid>
                    <Row className="legend">
                        <Col md={7} style={{ marginLeft: -10 }}>
                            {durations.map((duration, idx) => renderDurationLegend(duration, idx))}
                        </Col>
                        <Col md={2} className="colCheckbox">
                            <Form.Check type="checkbox" id='check-box-complete-days'>
                                <Form.Check.Input type="checkbox" checked={fullDays} onChange={(e) => setFullDays(!fullDays)} />
                                <Form.Check.Label>{t("txt_full_days")}</Form.Check.Label>
                            </Form.Check>
                        </Col>
                        <Col md={3} className="colCheckbox">
                            <Form.Check type="checkbox" id='check-box-complete-hours'>
                                <Form.Check.Input type="checkbox" checked={fullHours} onChange={(e) => setFullHours(!fullHours)} />
                                <Form.Check.Label>{t("txt_full_hours")}</Form.Check.Label>
                            </Form.Check>
                        </Col>
                    </Row>
                </Container>
            </div>
            <div className="timetable-wrapper">
                {loading ?
                    <div className="solutionLoader"><PulseLoader aria-label='Loading' /></div>
                    :
                    <table className="timetable">
                        <thead>
                            <tr className="header-dates">
                                <th></th>
                                {days.map((day, index) => {
                                    const dayOfMonth = parseInt(dateFormatter.format(day));
                                    const isFirstDayOfMonth = dayOfMonth === 1;
                                    const shouldRenderMonthName = isFirstDayOfMonth || (day.getMonth() !== days[index - 1]?.getMonth() && dayOfMonth < 27);

                                    return (
                                        <th key={day.toISOString()}>
                                            {shouldRenderMonthName && (
                                                <div className="month-cell">
                                                    {monthFormatter.format(day).toUpperCase()}, {day.getFullYear()}
                                                </div>
                                            )}

                                            <div className="day-cell">
                                                <span className="dayOfWeek">{dayFormatter.format(day).toUpperCase().slice(0, 1)}</span>
                                                {dayOfMonth}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {timeSlots.map((timeSlot) => (
                                <tr key={timeSlot}>
                                    <td className="time-cell">
                                        <span>{timeSlot.substring(0, timeSlot.length - 3)}</span>
                                    </td>
                                    {days.map((day) => (
                                        <td key={day.toISOString() + timeSlot}>
                                            {renderCellContent(day, timeSlot)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                }
            </div>

        </div>
    );
};

export default Timetable;
